import React, {
  useEffect,
  useState
} from "react";

import {
  Link,
  useLocation,
  useNavigate
} from "react-router-dom";

import {
  Home,
  Users,
  Building,
  DollarSign,
  FileText,
  BarChart3,
  Shield,
  User,
  Coffee,
  Package
} from "lucide-react";

import { getUser } from "../utils/auth";
import "./Sidebar.css";
import api from "../api";

function Sidebar({
  isOpen,
  setIsOpen
}) {
  const user =
    getUser();

  const role =
    user?.role;

  const location =
    useLocation();

  const navigate =
    useNavigate();

  const [
    nbAlertes,
    setNbAlertes
  ] = useState(0);

  // ==========================================================
  // ALERTES
  // ==========================================================

  useEffect(() => {

    // --------------------------------------------------------
    // Le Prestataire n'a pas besoin de charger les séjours.
    // --------------------------------------------------------

    if (role === "prestataire") {
      return;
    }

    api.get("/sejours")
      .then(res =>
        setNbAlertes(
          Array.isArray(res.data)
            ? res.data.length
            : 0
        )
      )
      .catch(err =>
        console.error(err)
      );

  }, [role]);

  // ==========================================================
  // DÉCONNEXION
  // ==========================================================

  const handleLogout = () => {
    localStorage.clear();

    navigate(
      "/",
      { replace: true }
    );
  };

  // ==========================================================
  // ITEM MENU
  // ==========================================================

  const NavItem = ({
    label,
    path,
    icon: Icon,
    badge
  }) => {

    const isActive =
      location.pathname === path ||
      (
        path !== "/dashboard" &&
        location.pathname.startsWith(
          `${path}/`
        )
      );

    return (
      <li>
        <Link
          to={path}
          className={`nav-btn ${
            isActive
              ? "active"
              : ""
          }`}
          onClick={() => {
            if (
              window.innerWidth <= 768
            ) {
              setIsOpen(false);
            }
          }}
        >

          <Icon size={18} />

          {isOpen && (
            <span className="nav-label">
              {label}
            </span>
          )}

          {badge > 0 && (
            <span className="badge">
              {badge}
            </span>
          )}

        </Link>
      </li>
    );
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      {/* =====================================================
          OVERLAY MOBILE
      ===================================================== */}

      {isOpen && (
        <div
          className="overlay"
          onClick={() =>
            setIsOpen(false)
          }
        />
      )}

      <aside
        className={`sidebar ${
          window.innerWidth <= 768
            ? (
                isOpen
                  ? "open"
                  : ""
              )
            : (
                isOpen
                  ? "open"
                  : "collapsed"
              )
        }`}
      >

        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="sidebar-header">

          <div className="logo">

            {isOpen ? (
              <>
                <div className="logo-alta">
                  ALTA
                </div>

                <div className="logo-immeuble">
                  IMMEUBLE
                </div>

                {role === "prestataire" && (
                  <div
                    style={{
                      fontSize:
                        "11px",
                      marginTop:
                        "4px",
                      opacity:
                        0.7
                    }}
                  >
                    PRESTATIONS
                  </div>
                )}
              </>
            ) : (
              <div className="logo-mini">
                AI
              </div>
            )}

          </div>

          <button
            className="toggle-btn"
            onClick={() =>
              setIsOpen(
                !isOpen
              )
            }
          >
            ☰
          </button>

        </div>

        {/* ===================================================
            NAVIGATION
        =================================================== */}

        <nav>
          <ul>

            {/* =================================================
                PRESTATAIRE
            ================================================= */}

            {role === "prestataire" && (
              <>

                <NavItem
                  label="Tableau de bord"
                  path="/dashboard"
                  icon={Home}
                />

                <NavItem
                  label="Prestations"
                  path="/blanchisserie"
                  icon={Coffee}
                />

                <NavItem
                  label="Reçus prestations"
                  path="/blanchisserie/recus"
                  icon={FileText}
                />

              </>
            )}

            {/* =================================================
                ADMIN
            ================================================= */}

            {role === "admin" && (
              <>

                <NavItem
                  label="Dashboard"
                  path="/admindashboard"
                  icon={Home}
                />

                <NavItem
                  label="Immeubles"
                  path="/immeubles"
                  icon={Building}
                />

                <NavItem
                  label="Utilisateurs"
                  path="/admin/users"
                  icon={User}
                />

                <NavItem
                  label="Logs"
                  path="/admin/logs"
                  icon={FileText}
                />

                <NavItem
                  label="Sécurité"
                  path="/admin/security"
                  icon={Shield}
                />

                <NavItem
                  label="Audit"
                  path="/admin/audit"
                  icon={BarChart3}
                />

                <NavItem
                  label="ota-sync"
                  path="/admin/ota-sync"
                  icon={FileText}
                />

              </>
            )}

            {/* =================================================
                COMPTABLE
            ================================================= */}

            {role === "comptable" && (
              <>

                <NavItem
                  label="Dashboard"
                  path="/dashboard"
                  icon={Home}
                />

                <NavItem
                  label="Réservations"
                  path="/reservations"
                  icon={FileText}
                />

                <NavItem
                  label="Immeubles"
                  path="/immeubles"
                  icon={Building}
                />

                <NavItem
                  label="PendingBookings"
                  path="/payment/pending-bookings"
                  icon={Users}
                />

                <NavItem
                  label="Clients"
                  path="/clients"
                  icon={Users}
                />

                <NavItem
                  label="Proformas"
                  path="/proformas"
                  icon={FileText}
                />

                <NavItem
                  label="Appartements"
                  path="/plan"
                  icon={Building}
                  badge={nbAlertes}
                />

                <NavItem
                  label="Revenus"
                  path="/revenus"
                  icon={DollarSign}
                />

                <NavItem
                  label="Revenus multi-canaux"
                  path="/statistiques/revenue-channels"
                  icon={DollarSign}
                />

                <NavItem
                  label="Prestations"
                  path="/blanchisserie"
                  icon={Coffee}
                />

                <NavItem
                  label="Reçus prestations"
                  path="/blanchisserie/recus"
                  icon={FileText}
                />

                <NavItem
                    label="Catalogue prestations"
                    path="/prestations/catalogue"
                    icon={Package}
                />

                <NavItem
                  label="Dépenses"
                  path="/depenses"
                  icon={BarChart3}
                />

                <NavItem
                  label="Factures"
                  path="/factures"
                  icon={FileText}
                />

                <NavItem
                  label="Reçus"
                  path="/recus"
                  icon={FileText}
                />

                <NavItem
                  label="Bilan"
                  path="/bilan"
                  icon={BarChart3}
                />

                <NavItem
                  label="Statistiques"
                  path="/statistiques"
                  icon={BarChart3}
                />

                <NavItem
                  label="Personnel"
                  path="/personnel"
                  icon={Users}
                />

                <NavItem
                  label="Salaires"
                  path="/salaires"
                  icon={DollarSign}
                />

                <NavItem
                  label="Remboursements"
                  path="/remboursements"
                  icon={DollarSign}
                />

              </>
            )}

            {/* =================================================
                RÉCEPTIONNISTE
            ================================================= */}

            {role === "receptionniste" && (
              <>

                <NavItem
                  label="Dashboard"
                  path="/dashboard"
                  icon={Home}
                />

                <NavItem
                  label="Réservations"
                  path="/reservations"
                  icon={FileText}
                />

                <NavItem
                  label="Clients"
                  path="/clients"
                  icon={Users}
                />

                <NavItem
                  label="Appartements"
                  path="/plan"
                  icon={Building}
                  badge={nbAlertes}
                />

                <NavItem
                  label="Proformas"
                  path="/proformas"
                  icon={FileText}
                />

                <NavItem
                  label="Revenus"
                  path="/revenus"
                  icon={DollarSign}
                />

                <NavItem
                  label="Prestations"
                  path="/blanchisserie"
                  icon={Coffee}
                />

                <NavItem
                  label="Reçus prestations"
                  path="/blanchisserie/recus"
                  icon={FileText}
                />

                <NavItem
                  label="Dépenses"
                  path="/depenses"
                  icon={BarChart3}
                />

                <NavItem
                  label="Factures"
                  path="/factures"
                  icon={FileText}
                />

                <NavItem
                  label="Reçus"
                  path="/recus"
                  icon={FileText}
                />

                <NavItem
                  label="Remboursements"
                  path="/remboursements"
                  icon={DollarSign}
                />

              </>
            )}

          </ul>
        </nav>

        {/* ===================================================
            FOOTER
        =================================================== */}

        <div className="sidebar-footer">

          <button
            onClick={
              handleLogout
            }
            className="logout-btn"
          >
            {isOpen
              ? "Déconnexion"
              : "⏻"}
          </button>

        </div>

      </aside>
    </>
  );
}

export default Sidebar;